'use client';

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaIndustry, FaPaintBrush, FaCog, FaHammer } from 'react-icons/fa';

const ServicesOverview = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Using a single brand color for consistency
  const brandColor = "#3b82f6";

  const overviewItems = useMemo(
    () => [
      {
        id: "industrial-services",
        title: "Industrial Services",
        description: "Specialized industrial services including waterproofing, epoxy flooring, protective coatings, and anti-corrosion systems for factories, plants, and industrial facilities across Qatar.",
        icon: <FaIndustry size={24} />,
        badge: "Specialized"
      },
      {
        id: "Professional Painting Services",
        title: "Professional Painting Services",
        description: "High-quality residential, commercial, and industrial painting services delivered by skilled teams, ensuring durable finishes and consistent workmanship across Qatar.",
        icon: <FaPaintBrush size={24} />,
        badge: "Premium"
      },
      {
        id: "mep-specialists",
        title: "MEP Services & Maintenance",
        description: "Comprehensive mechanical, electrical, and plumbing services including preventive maintenance, repairs, and system support for commercial and industrial facilities.",
        icon: <FaCog size={24} />,
        badge: "Expert"
      },
      {
        id: "concrete-construction",
        title: "Concrete & Civil Construction",
        description: "Concrete repair, epoxy flooring works, and civil construction support services for residential, commercial, and industrial projects throughout Doha and Qatar.",
        icon: <FaHammer size={24} />,
        badge: "Core Service"
      }
    ],

    []
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section
      style={{
        padding: '100px 20px',
        background: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}
      aria-labelledby="overview-title"
    >
      {/* Subtle background glow */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '400px',
          background: `linear-gradient(135deg, ${brandColor}08 0%, transparent 100%)`,
          pointerEvents: 'none',
        }}
      />

      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={{
          hidden: { opacity: 0, y: -30 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
        }}
        style={{
          maxWidth: '1200px',
          margin: '0 auto 80px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: `${brandColor}15`,
            borderRadius: '20px',
            marginBottom: '20px',
            border: `1px solid ${brandColor}25`,
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 600, color: brandColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Solutions
          </span>
        </div>

        <h2
          id="overview-title"
          style={{
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 900,
            color: '#111827',
            marginBottom: '16px',
            letterSpacing: '-1px',
            lineHeight: 1.2,
          }}
        >
          Comprehensive <br />
          <span style={{ background: `linear-gradient(135deg, ${brandColor}, #06b6d4)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Construction Services
          </span>
        </h2>

        <p
          style={{
            fontSize: '18px',
            color: '#6b7684',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.8',
            fontWeight: 500,
          }}
        >
          From construction manpower supply Qatar to specialized industrial services, we provide complete solutions tailored to your project.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <div style={{ width: '40px', height: '4px', background: brandColor, borderRadius: '2px' }} />
          <div style={{ width: '12px', height: '4px', background: brandColor, opacity: 0.3, borderRadius: '2px' }} />
        </div>
      </motion.div>

      <motion.div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '32px',
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        {overviewItems.map((item) => (
          <OverviewCard key={item.id} item={item} variants={cardVariants} brandColor={brandColor} />
        ))}
      </motion.div>
    </section>
  );
};

const OverviewCard = ({ item, variants, brandColor }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <motion.article
      variants={variants}
      whileHover={{ y: -12 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: '#ffffff',
        border: `1px solid ${isHovered ? brandColor + '40' : '#f1f5f9'}`,
        borderRadius: '24px',
        padding: '40px 32px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        boxShadow: isHovered
          ? `0 20px 40px ${brandColor}15, 0 8px 16px rgba(0,0,0,0.04)`
          : '0 4px 20px rgba(0, 0, 0, 0.02)',
      }}
    >
      {/* Unified Icon Style */}
      <div 
        style={{ 
          width: '64px', 
          height: '64px', 
          background: isHovered ? brandColor : `${brandColor}10`, 
          borderRadius: '20px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: isHovered ? '#ffffff' : brandColor,
          marginBottom: '28px',
          transition: 'all 0.3s ease',
        }}
      >
        {item.icon}
      </div>

      <div style={{ position: 'absolute', top: '40px', right: '32px' }}>
        <span style={{ 
          fontSize: '10px', 
          fontWeight: 800, 
          textTransform: 'uppercase', 
          letterSpacing: '1px', 
          color: isHovered ? brandColor : '#9ca3af' 
        }}>
          {item.badge}
        </span>
      </div>

      <h3
        style={{
          fontSize: '22px',
          fontWeight: 800,
          color: '#111827',
          marginBottom: '16px',
          letterSpacing: '-0.5px',
          lineHeight: 1.3
        }}
      >
        {item.title}
      </h3>

      <p
        style={{
          fontSize: '15px',
          color: '#6b7684',
          lineHeight: '1.7',
          marginBottom: '32px',
          flex: 1,
        }}
      >
        {item.description}
      </p>

      <motion.a
        href="/services"

        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          padding: '14px 20px',
          textAlign: 'center',   // Center the text
          background: isHovered ? brandColor : 'transparent',
          color: isHovered ? 'white' : '#111827',
          border: `1px solid ${isHovered ? brandColor : '#e2e8f0'}`,
          borderRadius: '12px',
          fontSize: '13px',
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          width: '100%',
          boxShadow: isHovered ? `0 4px 12px ${brandColor}30` : 'none',
        }}
      >
        View Details
      </motion.a>
    
      {/* Subtle bottom accent line */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '32px',
          right: '32px',
          height: isHovered ? '4px' : '0px',
          background: brandColor,
          borderRadius: '4px 4px 0 0',
          transition: 'all 0.3s ease',
        }}
      />
    </motion.article>
  );
};

export default ServicesOverview;